<template>
  <section
    ref="nsSearchbar"
    class="ns-searchbar"
    :class="{
      'cart-mode': mode === 'cart',
      'new-header': newHeader,
      focused,
      not_focused: !focused,
      slotted: theme === 'slotted',
      not_slotted: theme !== 'slotted'
    }"
  >
    <!-- input container-->
    <div
      class="input-container"
      :class="{ 'cart-mode': mode === 'cart' }"
      tabindex="0"
    >
      <!-- input -->
      <NsSearchbarInput
        :value="tempValue || searchValue"
        :translations="localTranslations"
        :new-header="newHeader"
        :focused="focused"
        :no-search-button="noSearchButton"
        :show-tooltip="showIconsContainerTooltip"
        @disable-input-tooltip="disableInputTooltip"
        @input="input"
        @focus="focus"
        @blur="blur"
        @analyze-key="analyzeKey"
        @clear-input="clearInput"
      />

      <!-- search listings -->
      <div id="searchListings" ref="searchListings" class="search-listings">
        <slot name="searchResults">
          <NsSearchbarList
            v-if="showRecentList"
            :mode="mode"
            :search-data="recentSearchData"
            :title="localTranslations.clearSearch"
            :translations="localTranslations"
            type="recent"
            @clear-recent-searches="clearRecentSearches"
            @toggle-value="toggleValue"
            @add-to-cart="addToCart"
            @click-search-item="selectSearchItem"
          />
          <NsSearchbarList
            v-if="showPredictiveList"
            :hide-see-more-results="hideSeeMoreResults"
            :mode="mode"
            :prices-loading="pricesLoading"
            :search-data="predictiveSearchData"
            :show-more-button="showMoreButton"
            :show-pricing-in-predictive="predictivePricing"
            :translations="localTranslations"
            type="predictive"
            @toggle-value="toggleValue"
            @click-search-item="selectSearchItem"
            @add-to-cart="addToCart"
            @see-more-results="seeMoreResults"
          />
          <NsSearchbarList
            v-if="showPopularList"
            :mode="mode"
            :search-data="popularSearchResults"
            :title="localTranslations.popularSearches"
            :translations="localTranslations"
            type="popular"
            @click-search-item="selectSearchItem"
            @toggle-value="toggleValue"
            @add-to-cart="addToCart"
          />
        </slot>

        <!-- for slotted search results -->
        <div class="search-section">
          <div v-for="(item, index) in slottedSearchedItems" :key="index">
            <slot :name="'ns-searchbar-slotted-searched-item-' + index" />
          </div>
        </div>
      </div>
    </div>

    <!-- search button -->
    <slot v-if="!noSearchButton && mode !== 'cart'" name="submitButton">
      <NsSearchbarButton
        :translations="localTranslations"
        :new-header="newHeader"
        :focused="focused"
        :submit-button-active="submitButtonActive"
        :show-tooltip="showSearchButtonTooltip"
        @disable-search-tooltip="disableSearchTooltip"
        @submit-search="submitSearch"
      />
    </slot>

    <!-- mobile search button -->
    <div v-if="newHeader" class="mobile-search-button" @click="focused = true">
      <NsIcon class="search-icon" icon-name="icon-search" />
      <span>{{ localTranslations.search }}</span>
    </div>
  </section>
</template>

<script>
import * as _ from "lodash";
import axios from "axios";
import { UserService } from "@nuskin/ns-account";
import {
  ConfigService,
  RunConfigService,
  SearchSessionStorageService,
  SearchBarAdapter,
  util,
  SitesLocalStorageService,
  SitesSessionStorageService,
  events,
  NsProductNotReleasedForSale,
  NsNotQualifiedException,
  StoreFrontSponsorStorageService,
  equinoxLocalStorage
} from "@nuskin/ns-util";
import {
  SolrQueryService,
  ProductSearchService,
  CartService,
  ProductStatusService,
  ProductService,
  AssessmentService,
  Agelocme,
  Product,
  PriceType,
  EquinoxCartService,
  ProductStatus,
  AdrService
} from "@nuskin/ns-shop";
import { NsIcon } from "@nuskin/design-components";
import { fromJsonString } from "@nuskin/ns-common-lib";
import { getConfiguration } from "@nuskin/configuration-sdk";

import NsSearchbarInput from "./NsSearchbarInput.vue";
import NsSearchbarList from "./NsSearchbarList.vue";
import NsSearchbarButton from "./NsSearchbarButton.vue";
import {
  getSearchParam,
  setSearchParam,
  clearSearchParam
} from "../utils/search";
import { MarketHeaderService, ProductDataService } from "@nuskin/ns-product";
import xss from "xss";

const KEY_BREADCRUMB_BACK = "breadcrumbBack";
const SEARCH_BUTTON_TOOLTIPS_KEY = "searchButtonTooltip";
const ICONS_CONTAINER_TOOLTIPS_KEY = "iconsContainerTooltip";
const SEARCH_TYPES = {
  recent: "recentSearch",
  predictive: "predictiveSearch",
  popular: "popularProductsSearch"
};

export default {
  name: "NsSearchbar",
  components: {
    NsIcon,
    NsSearchbarInput,
    NsSearchbarList,
    NsSearchbarButton
  },
  props: {
    addToCartOnClick: Boolean,
    context: {
      type: String,
      default: ""
    },
    disablePredictiveSearch: Boolean,
    disableSearchOnEnter: Boolean,
    hideSeeMoreResults: Boolean,
    mode: {
      type: String,
      default: ""
    },
    newHeader: Boolean,
    /**
     * Used to hide search button
     */
    noSearchButton: {
      type: Boolean,
      value: false
    },
    /**
     * Use orderTypes from search-options in the predictive results *** WILL
     * MAKE TWO CALLS TO SOLR AND WILL DECREASE PERFORMANCE -- only use if you
     * are sure you need it ***
     */
    orderTypeInPredictive: Boolean,
    popularSearchArray: {
      type: [String, Array],
      default: function() {
        return [];
      }
    },
    /**
     * This will be used eventually when set by analytical data.
     */
    popularSearchData: {
      type: Array,
      default: function() {
        return [];
      }
    },
    searchAgeloc: Boolean,
    /**
     * Used to change the search options from the defaults. All parameters in
     * the options are optional.  It should be an object that looks like this:
     * {
     *      "market": "US",
     *      "language": "en",
     *      "orderTypes": ["order", "zpo"],
     *      "predictiveLimit": 6,
     *      "resultTypes": 3,
     *      "searchFields": [1, 2, 7],
     *      "sortField": 2,
     *      "sortOrder": "desc"
     *  }
     */
    searchOptions: {
      type: [Object, String],
      default: function() {
        return {};
      }
      // observer: "_handleInputSearchOptions"
    },
    showPricingInPredictive: Boolean,
    slotted: Boolean,
    /**
     * Items to display as a search result in slotted view
     */
    slottedSearchedItems: {
      type: Array,
      default: function() {
        return [];
      }
    },
    theme: {
      type: String,
      default: ""
    },
    tooltipMax: {
      type: Number,
      default: 3
    },
    translations: {
      type: [Object, String],
      default: function() {
        return {};
      }
    },
    usingTooltips: Boolean
  },
  data: function() {
    return {
      agelocMeProduct: null,
      agelocMeCoreProduct: null,
      assessments: [],
      category: "Products",
      debouncedPredictiveSearch: _.debounce(this.typeAhead, 400),
      focused: false,
      initialShowSearchButtonTooltip: false,
      isSearchLoading: false,
      language: "",
      market: "",
      nsConfig: ConfigService.getMarketConfig(),
      recentSearchData: [],
      popularSearchResults: [],
      predictiveSearchData: [],
      previousQuery: "",
      psTotalItemsFound: 0,
      pricesLoading: false,
      searchRequestedHasFired: false,
      // passed to ns-search-results
      searchResults: [],
      searchValue: "",
      showIconsContainerTooltip: false,
      showMoreButton: false,
      showPredictiveList: false,
      showRecentList: false,
      showPopularList: false,
      showSearchButtonTooltip: false,
      tempValue: "",
      user: UserService.getUser(),
      serviceTranslations: {},
      searchAgeLocUseV2: undefined,
      serviceData: {},
      activeEvents: [],
      redirectPredictiveSearchToCart: false
    };
  },
  computed: {
    submitButtonActive() {
      return !!this.searchValue && this.searchValue.length >= 2;
    },
    localTranslations() {
      if (Object.keys(fromJsonString(this.translations) || {}).length == 0) {
        return this.serviceTranslations;
      } else {
        return fromJsonString(this.translations, {});
      }
    },
    predictivePricing() {
      return this.showPricingInPredictive || this.mode === "cart";
    },
    searchOnEnter() {
      if (this.disableSearchOnEnter) return false;
      return true;
    },
    searchOptionsObject() {
      return fromJsonString(this.searchOptions, {});
    }
  },
  watch: {
    // show search button tooltip when appropriate
    submitButtonActive(active) {
      if (active && this.usingTooltips && this.initialShowSearchButtonTooltip) {
        this.showSearchButtonTooltip = true;
        this.initialShowSearchButtonTooltip = false;
        const buttonTooltip =
          SitesLocalStorageService.getItem(SEARCH_BUTTON_TOOLTIPS_KEY)
            .showSearchButtonTooltip || 0;

        SitesLocalStorageService.writeItem(SEARCH_BUTTON_TOOLTIPS_KEY, {
          showSearchButtonTooltip: buttonTooltip + 1
        });
        setTimeout(() => {
          this.showSearchButtonTooltip = false;
        }, 4000);
      }
    },

    popularSearchArray() {
      this.getProductsFromSkus();
    }
  },
  async mounted() {
    let runConfig = RunConfigService.getRunConfig();
    this.language = runConfig.language ? runConfig.language : "en";
    this.market = runConfig.country ? runConfig.country : "US";
    this.redirectPredictiveSearchToCart = !!this.nsConfig
      .redirectPredictiveSearchToCart;

    await this.getServiceData(runConfig);

    if (Object.keys(fromJsonString(this.translations) || {}).length == 0) {
      this.serviceTranslations = this.serviceData.translations || {};
    }

    if (this.searchAgeLocUseV2 === undefined) {
      this.searchAgeLocUseV2 = !!this.serviceData.searchAgeLocUseV2;
    }
    this.checkTooltips();

    // Delay loading user assessments for 1 second to give page time to load user data.
    setTimeout(() => {
      if (this.searchAgeLocEnabled()) {
        this.loadUserAssessments();
      }
    }, 1000);

    if (this.popularSearchData && this.popularSearchData.length > 0) {
      this.getPopularProductsfromSearchData();
    } else {
      this.getProductsFromSkus();
    }

    this.searchValue = getSearchParam();
    if (this.searchValue) {
      this.submitSearch();
    }

    window.addEventListener("popstate", this.handleWindowPopState);
    events.getValue(events.salesevent.ACTIVE_EVENTS, this.setActiveSalesEvents);
  },
  destroyed() {
    window.removeEventListener("popstate", this.handleWindowPopState);
    events.unsubscribe(
      events.salesevent.ACTIVE_EVENTS,
      this.setActiveSalesEvents
    );
  },
  methods: {
    addToCart(event, item) {
      this.blur();
      // Solr converts boolean into String value of true
      if (item.isExclusive === "true" || item.isExclusive === true) {
        CartService.getAddToCartQty(item.sku)
          .then(itemQuantity => {
            if (itemQuantity > 0) {
              this.addItemToCart(item);
            } else {
              if (this.mode === "cart") {
                events.publish(events.shop.SHOW_EXCLUSIVE_OFFER_DETAILS, {
                  sku: item.sku
                });
              } else {
                this.doPDPRedirect(item);
              }
            }
          })
          .catch(error => {
            console.error("Failed to retrieve cart service", error);
            if (this.mode === "cart") {
              events.publish(events.shop.SHOW_EXCLUSIVE_OFFER_DETAILS, {
                sku: item.sku
              });
            } else {
              this.doPDPRedirect(item);
            }
          });
      } else {
        this.addItemToCart(item);
      }
    },

    async addItemToCart(item) {
      const userID = (this.user && this.user.id) || null;
      delete item.originalPrice;
      const options = {
        sku: item.sku,
        cntryCd: this.market.toUpperCase(),
        language: this.language,
        qty: 1,
        userId: userID,
        isAtpCheck: false,
        referrer: "search"
      };

      const config = (await getConfiguration(["Equinox_Markets"]))
        .Equinox_Markets;

      if (item.agelocme) {
        options.agelocme = item.agelocme;
      }

      //hide adddedToBag toast / popup since addSkuToCart triggers an CART_UPDATED event
      if (this.redirectPredictiveSearchToCart) {
        window.sessionStorage.setItem("hide-added-to-bag", true);
      }

      //@since current add to cart functionality will be deprecated
      //for the meantime add the quinox add to cart functionality here
      if (
        config.active &&
        config.country_code === this.market &&
        equinoxLocalStorage.isEquinoxEnabled()
      ) {
        try {
          //fetch product data from equinox so we can get the globalProductID
          const productData = await ProductDataService.getProductData(item.sku);
          const product = new Product(productData.products[0]);
          if (product.status !== ProductStatus.ReleasedForSale) {
            throw "Product is not sellable";
          }
          console.log(`Product Data`, productData);

          const cartOptions = {
            sku: item.sku,
            qty: 1,
            product: product,
            referrer: "productCard",
            domain: window.location.hostname,
            adr:
              (this.forceAdr && product.hasAdrOption()) ||
              (product.isAdrOnly() && !AdrService.isAdrOverride())
          };

          EquinoxCartService.addProductToEquinoxCart(cartOptions)
            .then(() => {
              cartOptions.add = {
                item: {
                  qty: cartOptions.qty
                }
              };

              events.publish(events.shop.ADD_TO_CART, cartOptions);
              events.publish(events.shop.SHOW_ADD_TO_BAG);
            })
            .catch(err => {
              // TODO
              console.error(err.response.data);
            });
        } catch (e) {
          console.error(e);
        }
      } else {
        CartService.addSkuToCart(options)
          .then(() => {
            if (this.redirectPredictiveSearchToCart) {
              //remove flag once item/product is added
              window.sessionStorage.removeItem("hide-added-to-bag", true);
              CartService.goToCartPage({
                isMySite: !!StoreFrontSponsorStorageService.getStoreFrontSponsor()
              });
            } else {
              events.publish(events.shop.SHOW_ADD_TO_BAG);
            }
          })
          .catch(err => {
            if (err instanceof NsNotQualifiedException) {
              events.publish(events.shop.SHOW_EXCLUSIVE_OFFER_DETAILS, {
                sku: options.sku
              });
            } else if (err instanceof NsProductNotReleasedForSale) {
              events.publish("solrSearchItemNotReleasedForSale", item.title);
            } else {
              events.publish("solrSearchGeneralError", err);
            }
          });
      }
    },

    setActiveSalesEvents(events) {
      if (events && Array.isArray(events) && events.length > 0) {
        this.activeEvents = [...events];
      } else {
        this.activeEvents = [];
      }
    },

    analyzeKey(e) {
      /*
       * Analyze what key has been pressed and the corresponding logic for the
       * said action.
       */
      const key = e.keyCode;
      const list = this.$refs.searchListings.querySelectorAll(
        "li.list-item, li.clear-recent-searches, li.see-more-results"
      );
      const listLength = list.length;
      const lastIndex = listLength - 1;
      /*
       *  Case Statement to manage what needs to be done depending on which key
       *  is pressed.
       */
      switch (key) {
        case 8: // Back Space
          this.tempValue = "";
          if (this.searchValue && this.searchValue.length === 0) {
            this.psTotalItemsFound = 0;
            this.predictiveSearchData = [];
          }
          if (!this.searchValue || this.searchValue.length < 2) {
            this.displaySearchLists();
          }
          break;
        case 13: // Enter Key
          if (this.searchOnEnter) {
            this.showSearchButtonTooltip = false;

            if (this.tempValue) {
              this.searchValue = this.tempValue;
            }

            this.tempValue = "";
            const activeItem = this.getActiveItem(list);
            let item = null;

            // if highlighted is a button then call it's method
            if (
              activeItem &&
              activeItem.item.classList.contains("clear-recent-searches")
            ) {
              this.clearRecentSearches();
            } else if (
              activeItem &&
              activeItem.item.classList.contains("see-more-results")
            ) {
              activeItem.item.classList.remove("active");
              const seeMoreIndex = activeItem.index;
              this.seeMoreResults();
              // highlight the next item that loads
              setTimeout(() => {
                const currentList = this.$refs.searchListings.querySelectorAll(
                  "li.list-item, li.clear-recent-searches, li.see-more-results"
                );
                currentList[seeMoreIndex].classList.add("active");
                this.tempValue = xss(currentList[seeMoreIndex].title);
              }, 300);
            }
            // if item is highlighted, call selectSearchItem instead of simple search
            else if (activeItem) {
              item = [
                ...this.recentSearchData,
                ...this.popularSearchResults,
                ...this.predictiveSearchData
              ].find(
                searchItem => searchItem.itemName === activeItem.item.title
              );
              this.selectSearchItem(item, activeItem.item.dataset.searchType);
            } else {
              if (this.mode !== "cart") {
                this.submitSearch();
              }
            }
          }
          break;
        case 27: // Esc Key
          this.clearInput();
          break;
        // Up Arrow
        case 38: {
          e.preventDefault();
          // Find out if an item is already active
          const activeItem = this.getActiveItem(list);
          let prevItemIndex = lastIndex;

          if (activeItem && activeItem.index > 0) {
            prevItemIndex = activeItem.index - 1;
          }

          if (activeItem) {
            activeItem.item.classList.remove("active");
          }

          list[prevItemIndex].classList.add("active");
          this.tempValue = xss(list[prevItemIndex].title);
          list[prevItemIndex].scrollIntoView({
            behavior: "smooth",
            block: "nearest"
          });
          break;
        }
        // Down Arrow
        case 40: {
          e.preventDefault();
          // Find out if an item is already active
          const activeItem = this.getActiveItem(list);
          let nextItemIndex = 0;

          if (activeItem && activeItem.index !== lastIndex) {
            nextItemIndex = activeItem.index + 1;
          }

          if (activeItem) {
            activeItem.item.classList.remove("active");
          }

          list[nextItemIndex].classList.add("active");
          this.tempValue = xss(list[nextItemIndex].title);
          list[nextItemIndex].scrollIntoView({
            behavior: "smooth",
            block: "nearest"
          });
          break;
        }
        default:
          break;
      }
    },

    blur() {
      document.body.removeEventListener("click", this.clickOutsideEvent);
      this.focused = false;

      const activeItem = this.getActiveItem();
      if (activeItem) {
        activeItem.item.classList.remove("active");
      }

      this.toggleSearchListings({ type: "blur" });
    },

    focus() {
      document.body.addEventListener("click", this.clickOutsideEvent);
      this.focused = true;
      this.toggleSearchListings({ type: "focus" });
      if (this.mode === "cart") {
        events.publish(events.shop.PRODUCT_SEARCH_CHANGED);
      }
    },

    checkTooltips() {
      if (this.usingTooltips) {
        const inputTooltip =
          SitesLocalStorageService.getItem(ICONS_CONTAINER_TOOLTIPS_KEY)
            .showIconsContainerTooltip || 0;

        const buttonTooltip =
          SitesLocalStorageService.getItem(SEARCH_BUTTON_TOOLTIPS_KEY)
            .showSearchButtonTooltip || 0;

        if (inputTooltip < this.tooltipMax) {
          this.showIconsContainerTooltip = true;
          SitesLocalStorageService.writeItem(ICONS_CONTAINER_TOOLTIPS_KEY, {
            showIconsContainerTooltip: inputTooltip + 1
          });

          setTimeout(() => {
            this.showIconsContainerTooltip = false;
          }, 6000);
        }

        if (buttonTooltip < this.tooltipMax) {
          this.initialShowSearchButtonTooltip = true;
        }
      }
    },

    cleanUpSearchTerm(searchTerm) {
      searchTerm = searchTerm.trim();
      searchTerm = searchTerm.replace(/®/g, "");
      searchTerm = searchTerm.replace(/©/g, "");
      return searchTerm.toLowerCase();
    },

    clearInput() {
      this.searchValue = "";

      if (this.tempValue) {
        this.tempValue = "";
      }

      this.focus();

      const activeItem = this.getActiveItem();
      if (activeItem) {
        activeItem.item.classList.remove("active");
      }

      this.psTotalItemsFound = 0;
      this.predictiveSearchData = [];
      this.showMoreButton = false;

      // Reflect the input to a property to determine whether to show the clear icon
      this.searchInputValue = this.searchValue;
    },

    clearRecentSearches() {
      SearchSessionStorageService.deleteRecentSearchData();
      this.recentSearchData = [];
      this.displaySearchLists();
    },

    clickOutsideEvent(event) {
      // Check that click was outside the el and it's children
      if (
        this.$refs.nsSearchbar &&
        !(
          this.$refs.nsSearchbar == event.target ||
          this.$refs.nsSearchbar.contains(event.target)
        )
      ) {
        this.blur();
      }
    },

    /*
     * Determine which of the 3 search dropdowns we should be displaying
     */
    displaySearchLists() {
      // hide all lists
      this.showPredictiveList = false;
      this.showRecentList = false;
      this.showPopularList = false;

      // if there's text, show predictive list only
      if (
        this.searchValue &&
        this.searchValue.length > 1 &&
        !this.disablePredictiveSearch
      ) {
        this.showPredictiveList = true;

        // else show recent and popular, (popular if not on mobile)
      } else if (this.recentSearchData.length > 0 && this.mode !== "cart") {
        this.showRecentList = true;
        this.showPopularList = !util.MobileUtils.isMobile();

        // if no recent data, show just popular
      } else if (this.mode !== "cart") {
        this.showPopularList = true;
      }
    },

    disableInputTooltip() {
      this.showIconsContainerTooltip = false;
      SitesLocalStorageService.writeItem(ICONS_CONTAINER_TOOLTIPS_KEY, {
        showIconsContainerTooltip: 100
      });
    },

    disableSearchTooltip() {
      this.showSearchButtonTooltip = false;
      SitesLocalStorageService.writeItem(SEARCH_BUTTON_TOOLTIPS_KEY, {
        showSearchButtonTooltip: 100
      });
    },

    doPDPRedirect(item) {
      SitesSessionStorageService.writeItem(KEY_BREADCRUMB_BACK, true);
      SearchSessionStorageService.setRecentSearchData(item);
      let href = "/content/";
      if (this.market.toLowerCase() === "jp") {
        href += "markets";
      } else {
        href += "nuskin";
      }
      href += `/${this.language}_${this.market}/products/product.${item.sku}${
        this.context ? "." + this.context : ""
      }.html`;
      window.location = href;
    },

    getActiveItem(list) {
      const items =
        list ||
        this.$refs.searchListings.querySelectorAll(
          "li.list-item, li.clear-recent-searches, li.see-more-results"
        );
      let itemObj = null;
      items.forEach((item, index) => {
        if (item.classList.contains("active")) {
          itemObj = { item: item, index };
        }
      });
      return itemObj;
    },

    getAgelocProduct(isCoreSolution) {
      let _this = this;
      const sku = isCoreSolution
        ? this.nsConfig.agelocme.coreSku
        : this.nsConfig.agelocme.sku;

      if (sku) {
        const options = {
          sku: sku,
          cntryCd: this.country,
          language: this.language,
          qty: 1,
          isAdr: false // add as order item by default. If item is adr only it will be set that way
        };

        ProductService.getProductBySku(options).then(response => {
          if (response.success) {
            if (isCoreSolution) {
              _this.agelocMeCoreProduct = response.product;
            } else {
              _this.agelocMeProduct = response.product;
            }
          }
        });
      }
    },

    /**
     * Used to query Solr the list of popular product skus and format them
     * correctly to display on popular search section
     */
    getPopularProductsfromSearchData() {
      SolrQueryService.getProductsBySKUArray(
        this.popularSearchData,
        this.language,
        this.market
      ).then(response => {
        const popularProducts = SearchBarAdapter.adaptPopularProductsResults(
          response,
          this.market,
          this.language
        );
        this.popularSearchResults = popularProducts;
      });
    },

    getProductsFromSkus() {
      const popularArray = fromJsonString(this.popularSearchArray, []);

      // check if popularArray is list of products (legacy) or list of skus (headerService)
      if (popularArray.length > 0 && typeof popularArray[0] === "object") {
        this.popularSearchResults = popularArray;
      } else {
        const promises = popularArray.map(sku => {
          // returns a promise that resolves with the product
          return ProductService.getProductData(sku);
        });

        Promise.all(promises).then(products => {
          const popularProducts = products.map(product => ({
            img: product.thumbnail,
            itemName: product.title,
            sku: product.sku
          }));
          this.popularSearchResults = popularProducts;
        });
      }
    },

    async getServiceData(runConfig) {
      if (Object.keys(this.serviceData).length > 0) return;
      const localeCode = `${runConfig.language || "en"}_${runConfig.country ||
        "US"}`;

      const siteUrl = window.location.origin.startsWith("http://localhost")
        ? runConfig.baseUrl || "https://test.nuskin.com"
        : window.location.origin;

      this.serviceData = await MarketHeaderService.getMarketHeaderData(
        `/content/nuskin/${localeCode}/home.html`,
        siteUrl,
        false,
        "searchbar"
      );
    },

    hasUser() {
      return this.user && this.user.id;
    },

    input(event) {
      this.searchValue = xss(event.target.value); //sanitize function
      this.tempValue = "";
      this.displaySearchLists();
      this.debouncedPredictiveSearch();
    },

    loadUserAssessments() {
      AssessmentService.getAssessmentsV2(data => {
        if (data.assessments) {
          this.assessments = data.assessments;
        }
        this.getAgelocProduct(false);
        this.getAgelocProduct(true);
      });
    },

    isProductOnSale({ price }) {
      for (const activeEvent of this.activeEvents) {
        if (
          price[`${activeEvent}-${PriceType.WWHL}`] ||
          price[`${activeEvent}-${PriceType.WRTL}`]
        ) {
          return true;
        }
      }
      return false;
    },

    getPriceType() {
      if (this.nsConfig.showWholeSalePricing) {
        return PriceType.WWHL;
      }
      if (
        this.hasUser() &&
        (this.user.isPreferredCustomer() || this.user.isDistributor())
      ) {
        return PriceType.WWHL;
      } else {
        return PriceType.WRTL;
      }
    },

    getEventName({ price }) {
      for (const activeEvent of this.activeEvents) {
        if (
          price[`${activeEvent}-${PriceType.WRTL}`] ||
          price[`${activeEvent}-${PriceType.WWHL}`]
        ) {
          return activeEvent;
        }
      }
      return null;
    },

    async getProductData(sku) {
      const {
        awsUrl,
        checkout: { clientId, clientSecret }
      } = this.nsConfig;
      const url = `${awsUrl}/productData/v1`;
      const payload = {
        locale: `${this.language}_${this.market}`,
        skus: [sku]
      };
      const productDataResponse = await axios.request({
        method: "POST",
        url,
        data: payload,
        headers: {
          "Content-Type": "application/json",
          client_id: clientId,
          client_secret: clientSecret
        }
      });
      const { data } = productDataResponse.data;
      if (!data || !data.products) return;
      return data.products[0];
    },
    // Send null for values you don't need
    async predictiveSearch(
      limit,
      fields,
      resultTypes,
      market,
      language,
      orderTypes
    ) {
      if (this.disablePredictiveSearch) return;
      const cartResultTypes = this.searchOptionsObject.resultTypes || 3; //All products and variants (No bases)

      // Get Default values for parameters
      const _limit = limit || this.searchOptionsObject.predictiveLimit || 6;
      const _fields = fields || this.searchOptionsObject.searchFields || null;
      const _resultTypes =
        resultTypes ||
        this.searchOptionsObject.resultTypes ||
        (this.mode === "cart" && cartResultTypes) ||
        null;
      const _market = market || this.searchOptionsObject.market || this.market;
      const _language =
        language || this.searchOptionsObject.language || this.language;
      const _orderTypes =
        orderTypes ||
        (this.orderTypeInPredictive && this.searchOptionsObject.orderTypes) ||
        null;

      const searchValue = this.cleanUpSearchTerm(this.searchValue);

      if (this.category === "Products" && searchValue.length > 1) {
        try {
          const {
            products,
            totalNumFound
          } = await ProductSearchService.quickSearch(
            searchValue,
            _fields,
            _limit,
            _market,
            _language,
            _resultTypes,
            _orderTypes
          );
          this.setPredictiveSearchData(products, totalNumFound);

          if (this.showPricingInPredictive || this.mode === "cart") {
            this.pricesLoading = true;
            const skus = products.map(product => product.sku);
            try {
              const productStatuses = await ProductStatusService.getProductStatus(
                skus,
                this.market.toUpperCase()
              );
              for (let product of products) {
                const productStatus = productStatuses.find(
                  status => status.SKU == product.sku
                );
                product.onSale = this.isProductOnSale(productStatus);
                product.setMarketAttributes(productStatus);

                if (product.onSale) {
                  this.pricesLoading = true;
                  const res = await this.getProductData(product.sku);
                  product.setProductData(res);
                } else {
                  product.addPricingFromStatus(
                    productStatus,
                    this.getPriceType()
                  );
                }
              }
            } finally {
              this.setPredictiveSearchData(products, totalNumFound);
              this.pricesLoading = false;
            }
          }
        } catch (error) {
          if (error === "timeout") {
            console.error("Solr timeout exceeded");
          } else {
            console.error("Exception in PredictiveSearch: ", error);
          }
        }
      }
    },

    searchAgeLocEnabled() {
      return (
        this.nsConfig.enableAssessmentSearch &&
        this.hasUser() &&
        (this.searchAgeloc || this.mode === "cart")
      );
    },

    searchAssessments(part) {
      let found = false;

      if (this.assessments && this.assessments.length > 0) {
        var regExp = new RegExp(part, "i");
        const matches = this.assessments.filter(
          assessment =>
            regExp.test(assessment.productCode || assessment.code) ||
            regExp.test(assessment.profileName)
        );

        if (matches.length > 0 && this.agelocMeProduct) {
          const matchProducts = matches.map(match => {
            const product =
              match.isCoreSolution && this.agelocMeCoreProduct
                ? new Product(this.agelocMeCoreProduct)
                : new Product(this.agelocMeProduct);

            product.agelocme = new Agelocme({
              code: match.productCode || match.code,
              isCoreSolution: match.isCoreSolution,
              label: match.profileName || match.label,
              name: match.name
            });

            return product;
          });

          this.setPredictiveSearchData(matchProducts);
          found = true;
        }
      }
      return found;
    },

    seeMoreResults() {
      const offset = this.searchOptionsObject.predictiveLimit || 6;
      const lastPage =
        this.predictiveSearchData.length + offset >= this.psTotalItemsFound;
      const limit = lastPage
        ? this.psTotalItemsFound
        : this.predictiveSearchData.length + offset;
      this.predictiveSearch(limit);
    },

    selectSearchItem(_item, searchType) {
      if (this.searchAgeLocUseV2 && !_item.itemName && _item.agelocme) {
        const name = _item.agelocme.code;
        _item.itemName = name;
      }

      if (_item.itemName) {
        // add title and fullImage props
        const item = { ..._item, title: _item.itemName, fullImage: _item.img };

        // call search events for analytics
        events.publish(
          events.shop.SEARCH_REQUESTED,
          {
            searchType: SEARCH_TYPES[searchType],
            typedQuery: this.searchValue,
            query: item.itemName,
            item: item
          },
          { includeDataObject: false, publishToGTM: true }
        );

        if (searchType === "predictive") {
          events.publish(events.shop.SEARCH_TYPEAHEAD, {
            typedQuery: this.searchValue,
            query: item.itemName,
            item: item
          });
        } else if (searchType === "popular") {
          events.publish(events.shop.SEARCH_POPULAR_PRODUCTS, { item });
        } else if (searchType === "recent") {
          events.publish(events.shop.SEARCH_RECENT, { item });
        }

        // If you are clicking on an item in predictive or popular search (and
        // not in cart mode), go to the UPDP for that SKU. Otherwise, do a
        // search for the text
        if (this.shouldRedirectToPDP(item)) {
          this.doPDPRedirect(item);
        } else if (this.addToCartOnClick || this.mode === "cart") {
          this.addItemToCart(item);
        } else if (
          this.mode !== "custom" &&
          this.mode !== "cart" &&
          !item.agelocme
        ) {
          // baiscally: else if recentSearch
          this.searchValue = xss(item.itemName);
          // this stops submitSearch from firing SEARCH_REQUESTED event again
          this.searchRequestedHasFired = true;
          this.submitSearch(item);
        }
        this.tempValue = "";
        this.blur();
      }
    },

    // "products" is based on the limit provided, "totalNumFound" is the total
    async setPredictiveSearchData(products, totalNumFound = null) {
      // Hide recent and popular searches from results bar if there are
      // predictive search results
      if (products.length > 0) {
        this.showRecentList = false;
        this.showPopularList = false;
      }

      let productArray = SearchBarAdapter.adaptLookAheadResults(
        products,
        this.market,
        this.language,
        this.getPriceType(),
        this.activeEvents
      );

      // enclosing it within the feature flag
      // so that this will be skipped if the flag is disabled
      if (equinoxLocalStorage.isEquinoxEnabled()) {
        // loop through productArray and query equinox API to get product status
        productArray = await Promise.all(
          productArray.map(async item => {
            const sku = item.sku;
            try {
              const productData = await ProductDataService.getProductData(sku);
              const product = new Product(productData.products[0]);

              //to hide add to cart button in NsSearchbarList.vue
              //simply set the sku to undefined
              if (product.status !== ProductStatus.ReleasedForSale) {
                delete item.sku;
              }
            } catch (error) {
              console.log(error);
            }
            return item;
          })
        );
      }

      events.publish("on-ns-searchbar-predictive-search-result", {
        detail: productArray
      });

      this.psTotalItemsFound = totalNumFound || products.length;
      this.predictiveSearchData = productArray;

      // set whether to show the "See more results button"
      const lastPage = products.length >= this.psTotalItemsFound;

      if (lastPage) {
        this.showMoreButton = false;
      } else {
        this.showMoreButton = true;
      }
    },

    shouldRedirectToPDP(item) {
      return (
        item.sku &&
        item.sku.length == 8 &&
        this.mode !== "cart" &&
        this.mode !== "custom" &&
        !item.agelocme
      );
    },

    getResults() {
      const searchResult =
        !!this.searchResults && this.searchResults.length > 0
          ? this.searchResults[0]
          : undefined;
      return searchResult ? searchResult.resultsList || [] : [];
    },

    async submitSearch(item, useReplaceState = false) {
      this.showSearchButtonTooltip = false;

      this.blur();

      // Start spinner
      events.publish("openSpinner");

      if (!this.isSearchLoading) {
        this.isSearchLoading = true;
        const unCleanValue = xss(this.searchValue);
        const value = this.cleanUpSearchTerm(this.searchValue);
        if (
          value !== "" &&
          (this.previousQuery !== value || this.mode === "cart")
        ) {
          // Set state
          this.previousQuery = value;
          this.searchValue = this.tempValue || this.searchValue;
          this.psTotalItemsFound = 0;
          this.predictiveSearchData = [];

          if (this.category === "Products") {
            // Make search request event call
            if (!this.searchRequestedHasFired) {
              events.publish(
                events.shop.SEARCH_REQUESTED,
                {
                  searchType: "productSearch",
                  typedQuery: this.searchValue,
                  query: unCleanValue
                },
                { includeDataObject: false, publishToGTM: true }
              );
            }

            this.searchRequestedHasFired = false;

            try {
              // Make the search
              const response = await ProductSearchService.search(
                value,
                this.searchOptionsObject.searchFields || null,
                this.searchOptionsObject.sortOrder || null,
                this.searchOptionsObject.sortField || null,
                this.searchOptionsObject.resultTypes || null,
                this.searchOptionsObject.orderTypes || null,
                this.searchOptionsObject.market || null,
                this.searchOptionsObject.language || null
              );

              // Set search query parameter
              setSearchParam(value, useReplaceState);

              // Store search in storage
              const storageItem = { itemName: value };
              if (item && item.img) {
                storageItem.img = item.img;
              }
              SearchSessionStorageService.setRecentSearchData(storageItem);

              // If result,
              if (typeof response !== "undefined") {
                // If result, remove search term from local storage
                localStorage.removeItem("SEARCH_TERM_KEY");

                // If using mode, clear input
                if (this.mode === "cart" || this.mode === "custom") {
                  this.clearInput();
                }

                // Set Search Results
                this.searchResults = SearchBarAdapter.adaptProductSearchResults(
                  response.products || response
                );

                // Make search event call
                events.publish(
                  events.shop.SEARCH_EXECUTED,
                  {
                    searchType: "fullSearch",
                    typedQuery: this.searchValue,
                    query: unCleanValue,
                    resultsObject: this.searchResults,
                    showSearchResults: !(
                      this.mode == "cart" || this.mode == "custom"
                    )
                  },
                  { includeDataObject: false, publishToGTM: true }
                );
              } else {
                this.searchResults = [];
              }

              // Get recent searches
              const recent = SearchSessionStorageService.getRecentSearchData();
              if (recent.length > 0) {
                this.recentSearchData = JSON.parse(recent);
              }
            } catch (error) {
              // Failed response

              // Error logs
              if (error === "timeout") {
                console.error("Solr timeout exceeded");
              } else {
                console.error("Exception in Product Search Service: ", error);
              }

              // Make search event call with empty array
              events.publish(
                events.shop.SEARCH_EXECUTED,
                {
                  searchType: "fullSearch",
                  typedQuery: this.searchValue,
                  query: unCleanValue,
                  resultsObject: [],
                  showSearchResults: !(
                    this.mode == "cart" || this.mode == "custom"
                  )
                },
                { includeDataObject: false, publishToGTM: true }
              );
            } finally {
              const results = this.getResults();
              if (results.length === 0) {
                events.publish(
                  events.shop.SEARCH_NO_RESULTS,
                  {
                    searchType: "searchNoResults",
                    typedQuery: this.searchValue,
                    query: unCleanValue,
                    resultsObject: [],
                    searchMode: this.mode === "cart" ? "cart" : "custom"
                  },
                  { includeDataObject: false, publishToGTM: true }
                );
              }
              // Close loaders
              this.isSearchLoading = false;
              events.publish("closeSpinner");
            }
          }
        } else {
          this.isSearchLoading = false;
          events.publish("closeSpinner");
        }
      }
    },

    toggleSearchListings({ type }) {
      //show search listing area
      switch (type) {
        case "focus":
          if (this.searchValue && this.searchValue.length > 0) {
            this.debouncedPredictiveSearch();
          } else {
            //Check for any recent searches stored in local storage
            const recent = SearchSessionStorageService.getRecentSearchData();
            if (recent.length > 0) {
              this.recentSearchData = JSON.parse(recent);
            }
          }
          this.displaySearchLists();
          break;
        case "blur":
          break;
      }
    },

    toggleValue(event) {
      // on mouseover soft select item, set background input value to current
      // soft selected item.
      const item = event.currentTarget;
      const activeItem = this.getActiveItem(
        this.$refs.searchListings.querySelectorAll("li.list-item")
      );
      if (event.type === "mouseover") {
        if (activeItem) {
          activeItem.item.classList.remove("active");
        }
        item.classList.add("active");
        this.tempValue = xss(item.title);
      } else if (event.type === "mouseout") {
        item.classList.remove("active");
        this.tempValue = "";
      }
    },

    typeAhead() {
      if (this.searchAgeLocEnabled()) {
        const value = this.cleanUpSearchTerm(this.searchValue);
        if (value.length > 1) {
          const found = this.searchAssessments(value);
          if (!found) {
            this.predictiveSearch();
          }
        }
      } else {
        this.predictiveSearch();
      }
    },

    clearSearch() {
      this.searchValue = "";
      this.previousQuery = "";
      clearSearchParam(true);

      this.$emit("close-search-results");
    },

    handleWindowPopState(event) {
      if (event) {
        this.searchValue = getSearchParam();
        if (this.searchValue) {
          this.submitSearch(undefined, true);
        } else {
          this.clearSearch();
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/colors";

.ns-searchbar {
  display: inline-flex;
  justify-content: center;
  width: 100%;
  max-width: 700px;
  -webkit-font-smoothing: antialiased;
  font-size: 14px;
  font-family: var(--font-family-primary);
  transition: var(--global-transition);
  // @include ns-searchbar-wrap;
  box-sizing: border-box;
  z-index: var(--z-modal);
  outline: none;

  &.new-header {
    max-width: 664px;
  }

  &.cart-mode {
    z-index: var(--z-tertiary);
    max-width: 1000px;
  }

  &.focused {
    border: var(--global-border-active);
  }
}

.input-container {
  position: relative;
  border: var(--global-border);
  width: 100%;
  max-width: 700px;
  color: var(--color-interactive-8);
  font-weight: var(--font-weight-regular);
  transition: border-color 0.15s ease-in-out;

  &:focus,
  &:active {
    outline: none;
  }

  &.cart-mode {
    background-color: #ffffff;
    max-width: 1000px;
  }
}

.new-header .input-container {
  display: none;
  border: 1px solid rgb(236, 237, 237);
  max-width: 664px;
}

.new-header.focused .input-container {
  display: block;
}

.slotted .input-container {
  display: flex;
}

.search-listings {
  position: absolute;
  top: calc(100% + 1px);
  left: -1px;
  border: var(--global-border);
  border-top: none;
  width: calc(100% + 2px);
  background-color: white;
  // background-color: var(--color-background-1);
  box-shadow: var(--header-box-shadow);
  opacity: 0;
  visibility: hidden;
  transition: none !important;
  z-index: var(--z-max);
  // @include ns-searchbar-search-listings();
}

.focused .search-listings {
  opacity: 1;
  visibility: visible;
}

.search-section {
  display: flex;
  flex-direction: column;
}

.mobile-search-button {
  display: flex;
  align-items: center;
  color: var(--color-interactive-9);
}

.search-icon {
  margin-right: 10px;
  height: 20px;
  width: 20px;
  color: var(--color-interactive-8);
}

@media (max-width: 767px) {
  .ns-searchbar.new-header.not_focused {
    justify-content: flex-end;
  }
}

@media (min-width: 768px) {
  .new-header .input-container {
    display: block;
  }

  .new-header .mobile-search-button {
    display: none;
  }
}

@media (max-width: 1023px) {
  .ns-searchbar {
    transition: none !important;
  }

  .not_focused .input-container {
    height: 35px;
  }

  .new-header.not_focused .input-container {
    height: 30px;
  }

  .input-container {
    width: 100%;
    max-width: 500px;

    &.cart-mode {
      max-width: 1000px;
      background-color: #ffffff;
    }
  }

  .focused .input-container {
    border: none;
    width: 100%;
    max-width: 100%;
  }

  .focused.not_slotted .input-container {
    position: fixed;
    top: 0;
    left: 0;
    padding: 8px 0;
    width: 100vw;
    background-color: var(--color-background-1);
    z-index: 999;

    &.cart-mode {
      padding: 0;
    }
  }

  .focused.not_slotted .search-listings {
    left: 0;
    top: 100%;
    border: none;
    height: 100vh;
    width: 100%;
    background-color: var(--color-background-1);
  }
}

@media (min-width: 1024px) {
  .ns-searchbar {
    border: none !important;
  }

  .new-header.ns-searchbar {
    justify-content: flex-start;
  }

  .ns-searchbar.focused.not_slotted {
    border: none;
  }

  .focused .input-container {
    border: var(--global-border-active);
  }

  .input-container {
    height: 35px;
  }

  .new-header .input-container {
    height: 30px;
  }

  .search-listings {
    transition: var(--global-transition);
  }
}
</style>
